import { cloneDeep } from 'lodash';
import { createId } from '../../id.helpers';
import { omit } from '../../object.helpers';
import {
  getNextColorIndexForAppendedProposal,
  normalizeProposalsColorIndices,
} from '../../proposal.helpers';
import { createProposal } from '../../proposal-factory.helpers';
import { getProposals } from '../../proposals/proposals.helpers';
import { findFreeName } from '../../string.helpers';
import type { Baseline } from '@shared/models/project.model';
import type { ActionId, ProposalId } from '@shared/models/proposals.model';
import type { Action } from '../update-engine.models';

/** Lookup of {@link Baseline.actions} by id. */
const getVersionActionsById = (version: Baseline): Map<ActionId, Action> =>
  new Map((version.actions ?? []).map((action) => [action.id, action]));

/**
 * True when every id in {@link actionIds} exists on {@link Baseline.actions}.
 * Missing ids are skipped when cloning; skip-enrichment must not inherit
 * {@link IProposal.resultsRecord} unless this is true.
 */
export const areAllProposalActionIdsPresent = (
  version: Baseline,
  actionIds: readonly ActionId[],
): boolean => {
  const byId = getVersionActionsById(version);
  return actionIds.every((id) => byId.has(id));
};

/**
 * Deep-clones {@link Action}s referenced by {@link sourceActionIds} onto new ids so the
 * duplicate never shares {@link Baseline.actions} with the source (DEV-3268).
 * Missing ids are skipped.
 */
const cloneActionsForProposalDuplicate = (
  version: Baseline,
  sourceActionIds: readonly ActionId[],
): { actions: Action[]; actionIds: ActionId[] } => {
  const byId = getVersionActionsById(version);
  const actions: Action[] = [];
  const actionIds: ActionId[] = [];

  for (const sourceId of sourceActionIds) {
    const source = byId.get(sourceId);
    if (!source) continue;
    const cloned: Action = { ...cloneDeep(source), id: createId({}) };
    actions.push(cloned);
    actionIds.push(cloned.id);
  }

  return { actions, actionIds };
};

/**
 * Duplicate an {@link IProposal}: clone its actions onto new ids, copy results and
 * report fields, pick a free name and colour, and insert the copy after the source.
 *
 * Returns the same {@link version} reference when {@link proposalId} is not found.
 *
 * @param version - Building version that owns the source proposal
 * @param proposalId - Source {@link IProposal.id}
 */
export const duplicateProposal = (
  version: Baseline,
  proposalId: ProposalId,
): Baseline => {
  const proposals = getProposals(version);
  const sourceIndex = proposals.findIndex((p) => p.id === proposalId);
  const source = proposals[sourceIndex];
  if (!source) return version;

  const { actions: clonedActions, actionIds } =
    cloneActionsForProposalDuplicate(version, source.actionIds);

  const name = findFreeName(
    proposals.map((p) => p.name),
    source.name,
  );
  const colorIndex = getNextColorIndexForAppendedProposal(proposals);
  // Drop identity fields; createProposal assigns a new id and the mutator sets colour + cloned actions.
  const sourceRest = omit(source, 'id', 'colorIndex', 'actionIds');
  const newProposal = createProposal({
    ...sourceRest,
    name,
    colorIndex,
    actionIds,
  });

  const nextProposals = [...proposals];
  nextProposals.splice(sourceIndex + 1, 0, newProposal);

  return {
    ...version,
    actions: [...(version.actions ?? []), ...clonedActions],
    proposals: normalizeProposalsColorIndices(nextProposals),
  };
};
